// components/ProjectGoogleMap.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { IGeoLocationList } from "../../project";
import { defaultMapCenter, envConfig, getProjectColor } from "../../../../utils/config";
import { formatBudget } from "../../../../utils/formateNumber";
import { Link, useLocation } from "react-router-dom";

const DEFAULT_MAP_HEIGHT = "500px";
const MAX_ZOOM = 10;
const createMarkerIcon = (color: string) =>
  `data:image/svg+xml;utf-8,
    <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='${color}'>
      <path d='M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z'/>
    </svg>`;

type Props = {
  allGeoLocations: IGeoLocationList[];
  activeProjectIndex?: string | number | null;
  color?: string;
  height?: string;
  /** Extra space north of the pins, as a fraction of their latitude span. */
  expandNorthRatio?: number;
};

export default function ProjectGoogleMap({
  allGeoLocations,
  activeProjectIndex = null,
  color = "",
  height = DEFAULT_MAP_HEIGHT,
  expandNorthRatio = 0,
}: Props) {
  const mapContainerStyle = { width: "100%", height };
  const location = useLocation();  
  let isLoaded=true;
  
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapCenter = useMemo(
    () => allGeoLocations.length > 0
      ? {
        lat: allGeoLocations[0].geo_lat,
        lng: allGeoLocations[0].geo_long,
      }
      : defaultMapCenter,
    [allGeoLocations],
  );

  const [selectedLocation, setSelectedLocation] =
    useState<IGeoLocationList | null>(null);
  const onMarkerClick = useCallback((loc: IGeoLocationList) => {
    setSelectedLocation(loc);
  }, []);

  const fitMapWithZoomLimit = (map: google.maps.Map) => {
    const bounds = new google.maps.LatLngBounds();

    allGeoLocations.forEach(loc => {
      bounds.extend({ lat: loc.geo_lat, lng: loc.geo_long });
    });

    if (expandNorthRatio > 0 && !bounds.isEmpty()) {
      const north = bounds.getNorthEast().lat();
      const south = bounds.getSouthWest().lat();
      const span = Math.max(north - south, 1);
      bounds.extend({
        lat: north + span * expandNorthRatio,
        lng: bounds.getCenter().lng(),
      });
    }

    map.fitBounds(bounds);

    // prevent over-zooming
    google.maps.event.addListenerOnce(map, "idle", () => {
      if (map.getZoom()! > MAX_ZOOM) {
        map.setZoom(MAX_ZOOM);
      }
    });
  };

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;

    if (allGeoLocations.length > 0) {
      fitMapWithZoomLimit(map);
    }
  };
  useEffect(() => {
    if (!mapRef.current || allGeoLocations.length === 0) return;
    fitMapWithZoomLimit(mapRef.current);
  }, [allGeoLocations, expandNorthRatio]);

  return (
    <>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={7}
          onLoad={onLoad}
        >
          {/* Markers */}
          {allGeoLocations.map((loc, index) => {
            const markerColor = color!==""?color:getProjectColor(loc.project_index);
            return (
              <Marker
                key={`${loc.loc_id}-${index}`}
                position={{ lat: loc.geo_lat, lng: loc.geo_long }}
                icon={{
                  url: createMarkerIcon(markerColor),
                  scaledSize: new window.google.maps.Size(34, 34),
                }}
                title={`${loc.project_name}`}
                onClick={() => onMarkerClick(loc)}
                opacity={
                  activeProjectIndex == null ||
                    loc.project_index === activeProjectIndex ? 1 : 0.4
                }
              />
            );
          })}

          {/* InfoWindow */}
          {selectedLocation && (
            <InfoWindow
              position={{
                lat: selectedLocation.geo_lat,
                lng: selectedLocation.geo_long,
              }}
              onCloseClick={() => setSelectedLocation(null)}
              options={{
                headerDisabled: true,
                pixelOffset: new window.google.maps.Size(0, -20),
              }}
            >
              <div>
                <div className="mb-5 d-flex align-items-start flex-sm-row flex-column fs-4 w-100 gap-5">
                  <Link to={`/manage-project/${selectedLocation.project_index}/${location.search}`} state={{ from: location.state?.from || location.pathname, }} className="text-decoration-none text-black"><strong>{selectedLocation.project_name}</strong></Link>
                  <p
                    onClick={() => setSelectedLocation(null)}
                    style={{
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    ✕
                  </p>
                </div>

                <div className="d-flex justify-content-between flex-column-reverse flex-sm-row gap-0 gap-sm-5">
                  <div>
                    <div className="mb-2">
                      <strong>Location:</strong> {selectedLocation.geo_location}
                    </div>

                    {selectedLocation.erf_number && (
                      <div className="mb-2">
                        <strong>ERF:</strong> {selectedLocation.erf_number}
                      </div>
                    )}

                    {selectedLocation.sector_name && (
                      <div>
                        <strong>Sector:</strong> {selectedLocation.sector_name}
                      </div>
                    )}
                  </div>
                  <div className="text-end d-flex flex-row flex-sm-column gap-1 align-items-center align-items-sm-end mb-2 mb-sm-0">
                    <strong>Budget:</strong>
                    <p className="mb-0 fw-bold fs-3">{`${formatBudget(selectedLocation.budget)}`}</p>
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        <p>Loading map…</p>
      )}
    </>
  );
}
